import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UserParameter.css';
import { useNavigate, Link } from 'react-router-dom';
import WhatsAppIntegration from './WhatsAppIntegration';
import ChatbotSettings from './ChatbotSettings';
import { useTranslation } from 'react-i18next';
import { FaUser, FaEnvelope, FaLock, FaKey, FaTrash, FaCog, FaInstagram, FaGoogle, FaWhatsapp, FaRobot, FaWarehouse, FaTruck, FaTimes, FaPlus, FaLink, FaSave, FaCheck, FaCheckCircle, FaExclamationTriangle, FaEdit, FaCreditCard, FaEye, FaEyeSlash, FaShoppingCart, FaBan } from 'react-icons/fa';

const UserParameter = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(undefined);
  const [isExperienceUser, setIsExperienceUser] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [popupMessage, setPopupMessage] = useState('');
  const [isPasswordPopupVisible, setIsPasswordPopupVisible] = useState(false);
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [confirmUsername, setConfirmUsername] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showWhatsAppIntegration, setShowWhatsAppIntegration] = useState(false);
  const [showChatbotSettings, setShowChatbotSettings] = useState(false);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [isWmsConnecting, setIsWmsConnecting] = useState(false);
  const navigate = useNavigate();
  const [showPasswordMap, setShowPasswordMap] = useState({});
  const [outlook_email, setOutlookEmail] = useState('');
  const [isRegisteringWebhook, setIsRegisteringWebhook] = useState(false);
  const [isRemovingWebhook, setIsRemovingWebhook] = useState(false);
  const [isRegisteringSaveOnlyWebhook, setIsRegisteringSaveOnlyWebhook] = useState(false);
  const [outlookAccounts, setOutlookAccounts] = useState([]);
  const [isLoadingOutlookAccounts, setIsLoadingOutlookAccounts] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    
    const userDataString = localStorage.getItem('userData');
    if (userDataString) {
        try {
            const parsedUserData = JSON.parse(userDataString);
            if (parsedUserData.role === 'experience') {
                setIsExperienceUser(true);
                setUser({});
                return; 
            }
        } catch (e) {
            console.error("Error parsing user data for experience check", e);
        }
    }

    fetchUserData(token);
    fetchOutlookAccounts(); // Add this line
  }, [navigate]);

  useEffect(() => {
    if (user === null && !isExperienceUser) {
      navigate('/');
    }
  }, [user, navigate, isExperienceUser]);

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setUser({
          username: response.data.username || '',
          firstname: response.data.firstname || '',
          lastname: response.data.lastname || '',
          email: response.data.email || '',
          subscription_plan: response.data.subscription_plan || '',
          user_meta_id: response.data.user_meta_id || '',
          user_insta_id: response.data.user_insta_id || '',
          gmail_email_address: response.data.gmail_email || '',
          automation: response.data.automation || '',
          shop_name: response.data.shop_name || '',
          instagram_username: response.data.insta_username || '',
          whatsapp_phone_number: response.data.whatsapp_phone_number || '',
          edn_api_account: response.data.edn_api_account || '',
          unanimity_wms_id: response.data.unanimity_wms_id || '',
          outlook_email: response.data.outlook_email || '',
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUser(null);
    }
  };

  const checkConnectionStatuses = () => {
    if (isExperienceUser) return;
    const urlParams = new URLSearchParams(window.location.search);
    const instaStatus = urlParams.get('status');
    const googleStatus = urlParams.get('google_status');
    const shopifyStatus = urlParams.get('shopify_status');
    const outlookStatus = urlParams.get('outlook_status');

    if (instaStatus === 'success') {
      setPopupMessage('Account connected successfully!');
    } else if (instaStatus === 'failure') {
      setPopupMessage('Failed to connect Instagram account.');
    }

    if (googleStatus === 'success') {
      setPopupMessage('Gmail connected successfully!');
    } else if (googleStatus === 'failure') {
      setPopupMessage('Failed to connect Google account.');
    }

    if (shopifyStatus === 'success') {
      setPopupMessage('Shopify store connected successfully!');
    } else if (shopifyStatus === 'failure') {
      setPopupMessage('Failed to connect Shopify account.');
    }

    if (outlookStatus === 'success') {
      setPopupMessage('Outlook connected successfully!');
    } else if (outlookStatus === 'failure') {
      setPopupMessage('Failed to connect Outlook account.');
    }
  };

  const handlePasswordSubmit = async (e) => {
    if (isExperienceUser) return;
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      alert('New passwords do not match.');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.put('/api/users/update-user', {
        currentPassword,
        password: newPassword,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      alert('Password updated successfully.');
      setIsPasswordPopupVisible(false);
    } catch (error) {
      console.error('Failed to update password:', error);
      alert('Error updating password.');
    }
  };

  const handleProfileUpdate = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.put('/api/users/update-user', {
        username: user.username,
        whatsapp_phone_number: user.whatsapp_phone_number
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Profile updated successfully.');
      fetchUserData(token);
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Error updating profile.');
    }
  };

  const handleConnectInstagram = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/instagram/connect', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to connect Instagram account:', error);
    }
  };

  useEffect(() => {
    if (!isExperienceUser) {
      checkConnectionStatuses();

      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      if (code && state) {
        handleInstagramCallback(code, state);
      }
    }
  }, [isExperienceUser]);

  const handleInstagramCallback = async (code, state) => {
    if (isExperienceUser) return;
    const data = await fetchInstagramAccounts(code, state);
    if (data) {
      setInstagramAccounts(data.accounts);
    }
  };

  const fetchInstagramAccounts = async (code, state) => {
    if (isExperienceUser) return null;
    try {
      const response = await axios.get(`/api/social/instagram/callback?code=${code}&state=${state}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch Instagram accounts:', error);
      return null;
    }
  };

  const handleAccountSelect = async (accountId, accountName) => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('You are not logged in. Please log in again.');
        navigate('/login');
        return;
      }

      const payload = {
        account_id: accountId,
        name: accountName
      };

      console.log('Sending payload:', payload);

      await axios.post('/api/social/instagram/select_account', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      navigate('/parameters?status=success');
    } catch (error) {
      if (error.response && error.response.status === 401) {
        alert('Session expired. Please log in again.');
        navigate('/login');
      } else {
        console.error('Failed to select Instagram account:', error);
        alert('Failed to connect Instagram account.');
      }
    }
  };

  const handleConnectGoogle = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/google/connect', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to connect Google account:', error);
      setPopupMessage('Failed to connect Google account.');
    }
  };

  const togglePasswordPopup = () => {
    if (isExperienceUser) return;
    setIsPasswordPopupVisible(!isPasswordPopupVisible);
  };

  const handleDisconnectInstagram = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/social/instagram/unsubscribe', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUser({ ...user, user_insta_id: '', instagram_username: '' });
      setPopupMessage('Instagram account disconnected successfully!');
    } catch (error) {
      console.error('Failed to disconnect Instagram account:', error);
      setPopupMessage('Failed to disconnect Instagram account.');
    }
  };

  const handleDisconnectGoogle = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/social/google/unsubscribe', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUser({ ...user, gmail_email_address: '' });
      setPopupMessage('Google account disconnected successfully!');
    } catch (error) {
      console.error('Failed to disconnect Google account:', error);
      setPopupMessage('Failed to disconnect Google account.');
    }
  };

  const handleDisconnectWhatsApp = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/social/disconnect/whatsappbusiness', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUser({ ...user, whatsapp_phone_number: '' });
      setPopupMessage('WhatsApp account disconnected successfully!');
    } catch (error) {
      console.error('Failed to disconnect WhatsApp account:', error);
      setPopupMessage('Failed to disconnect WhatsApp account.');
    }
  };

  const handleDeleteUser = async () => {
    if (isExperienceUser) return;
    if (confirmUsername !== user.username) {
      alert('Username does not match.');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete('/api/users/user/delete', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      localStorage.removeItem('access_token');
      navigate('/');
    } catch (error) {
      console.error('Failed to delete user account:', error);
      alert('Error deleting user account.');
    }
  };

  const toggleDeletePopup = () => {
    if (isExperienceUser) return;
    setIsDeletePopupVisible(!isDeletePopupVisible);
  };

  const handleCloseWhatsAppIntegration = () => {
    if (isExperienceUser) return;
    setShowWhatsAppIntegration(false);
  };

  const handleConnectWhatsApp = () => {
    if (isExperienceUser) return;
    setShowWhatsAppIntegration(true);
  };

  const handleOpenChatbotSettings = () => {
    if (isExperienceUser) return;
    setShowChatbotSettings(true);
  };

  const handleCloseChatbotSettings = () => {
    if (isExperienceUser) return;
    setShowChatbotSettings(false);
  };

  const handleConnectWMS = async () => {
    if (isExperienceUser) return;
    try {
      setIsWmsConnecting(true);
      const token = localStorage.getItem('access_token');
      await axios.post('/api/users/associate-wms', null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPopupMessage('Successfully connected to Unanimity WMS!');
      fetchUserData(token);
    } catch (error) {
      console.error('Failed to connect to WMS:', error);
      if (error.response?.status === 403) {
        setPopupMessage('Only admin and logistics users can connect to WMS.');
      } else {
        setPopupMessage('Failed to connect to Unanimity WMS.');
      }
    } finally {
      setIsWmsConnecting(false);
    }
  };

  const handleConnectOutlook = async () => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/outlook/connect', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to connect Outlook account:', error);
      setPopupMessage('Failed to connect Outlook account.');
    }
  };

  const handleDisconnectOutlook = async (outlookEmail) => {
    if (isExperienceUser) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/social/outlook/unsubscribe/${outlookEmail}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPopupMessage(`Outlook account ${outlookEmail} disconnected successfully!`);
      // Refresh the accounts list and user data
      fetchOutlookAccounts();
      fetchUserData(token);
    } catch (error) {
      console.error('Failed to disconnect Outlook account:', error);
      setPopupMessage('Failed to disconnect Outlook account.');
    }
  };

  const handleRegisterOrderWebhook = async () => {
    if (isExperienceUser) return;
    try {
      setIsRegisteringWebhook(true);
      const token = localStorage.getItem('access_token');
      
      console.log('Registering webhook, token exists:', !!token);
      
      const response = await axios.post('/api/orders/shopify_live_webhooks/register_order_webhook', null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log('Webhook registration response:', response);
      
      alert('Shopify order webhook registered successfully!');
      setPopupMessage('Shopify order webhook registered successfully!');
    } catch (error) {
      console.error('Failed to register Shopify order webhook:', error);
      console.error('Error details:', error.response || error.message || error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      alert(`Failed to register Shopify order webhook: ${errorMessage}`);
      setPopupMessage(`Failed to register Shopify order webhook: ${errorMessage}`);
    } finally {
      setIsRegisteringWebhook(false);
    }
  };

  const handleRemoveOrderWebhook = async () => {
    if (isExperienceUser) return;
    try {
      setIsRemovingWebhook(true);
      const token = localStorage.getItem('access_token');
      
      console.log('Removing webhooks, token exists:', !!token);
      
      const response = await axios.delete('/api/orders/shopify_webhooks/remove_order_shopify_webhooks', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log('Webhook removal response:', response);
      
      const message = response.data.message || 'Shopify order webhooks removed successfully!';
      alert(message);
      setPopupMessage(message);
    } catch (error) {
      console.error('Failed to remove Shopify order webhooks:', error);
      console.error('Error details:', error.response || error.message || error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      alert(`Failed to remove Shopify order webhooks: ${errorMessage}`);
      setPopupMessage(`Failed to remove Shopify order webhooks: ${errorMessage}`);
    } finally {
      setIsRemovingWebhook(false);
    }
  };

  const handleRegisterSaveOnlyWebhook = async () => {
    if (isExperienceUser) return;
    try {
      setIsRegisteringSaveOnlyWebhook(true);
      const token = localStorage.getItem('access_token');
      
      console.log('Registering save-only webhook, token exists:', !!token);
      
      const response = await axios.post('/api/orders/shopify_webhooks/register_order_shopify_webhooks', null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log('Save-only webhook registration response:', response);
      
      alert('Shopify save-only order webhook registered successfully!');
      setPopupMessage('Shopify save-only order webhook registered successfully!');
    } catch (error) {
      console.error('Failed to register Shopify save-only order webhook:', error);
      console.error('Error details:', error.response || error.message || error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      alert(`Failed to register Shopify save-only order webhook: ${errorMessage}`);
      setPopupMessage(`Failed to register Shopify save-only order webhook: ${errorMessage}`);
    } finally {
      setIsRegisteringSaveOnlyWebhook(false);
    }
  };

  const fetchOutlookAccounts = async () => {
    if (isExperienceUser) return;
    try {
      setIsLoadingOutlookAccounts(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/social/outlook/accounts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOutlookAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Failed to fetch Outlook accounts:', error);
    } finally {
      setIsLoadingOutlookAccounts(false);
    }
  };

  const renderBillingSection = () => {
    if (isExperienceUser || !user || !user.subscription_plan) return null;
    return (
      <div className="billing-info-section">
        <h2 className="section-title"><FaCreditCard /> {t('userParameter.billing')}</h2>
        <div className="billing-info-content">
          <div className="info-item">
            <FaCreditCard className="info-icon" />
            <div>
              <strong>{t('userParameter.currentPlan')}</strong>
              <p>{user.subscription_plan || t('userParameter.freePlan')}</p>
            </div>
          </div>
          <button 
            className="manage-billing-button" 
            onClick={() => navigate('/billing')}
          >
            <FaCreditCard /> {t('userParameter.manageBilling')}
          </button>
        </div>
      </div>
    );
  };

  if (isExperienceUser) {
    return (
      <div className="user-parameter-container experience-notice-container">
        <div className="experience-notice-card">
          <FaBan size={50} className="experience-notice-icon" />
          <h2>{t('userParameter.experience.title', 'Settings Not Available')}</h2>
          <p>{t('userParameter.experience.message', 'The user parameters page is currently not available for experience accounts. Please explore other features of the application.')}</p>
          <button onClick={() => navigate('/dashboard')} className="primary-button">
            {t('userParameter.experience.backToDashboard', 'Back to Dashboard')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-parameter-container">
      {user === undefined && !isExperienceUser ? (
        <div className="loading">{t('userParameter.loading')}</div>
      ) : user ? (
        <div className="user-parameter-content">
          <div className="user-parameter-left">
            <div className="user-info-section">
              <h2 className="section-title"><FaUser /> {t('userParameter.userInfo')}</h2>
              <div className="user-info-grid">
                <div className="info-item">
                  <FaUser className="info-icon" />
                  <div>
                    <strong>{t('userParameter.username')}</strong>
                    <p>{user.username}</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaEnvelope className="info-icon" />
                  <div>
                    <strong>{t('userParameter.email')}</strong>
                    <p>{user.email}</p>
                  </div>
                </div>
              </div>
              <button className="change-password-button" onClick={togglePasswordPopup}>
                <FaLock /> {t('userParameter.changePassword')}
              </button>
            </div>

            {renderBillingSection()}

            <div className="integrations-section">
              <h2 className="section-title"><FaCog /> {t('userParameter.integrations')}</h2>
              <div className="integration-buttons">
                <IntegrationButton
                  icon={<FaInstagram />}
                  label="Instagram"
                  connected={!!user.user_insta_id}
                  onConnect={handleConnectInstagram}
                  onDisconnect={handleDisconnectInstagram}
                  className="instagram"
                />
                <IntegrationButton
                  icon={<FaGoogle />}
                  label="Google"
                  connected={!!user.gmail_email_address}
                  onConnect={handleConnectGoogle}
                  onDisconnect={handleDisconnectGoogle}
                  className="google"
                />
                <IntegrationButton
                  icon={<FaWhatsapp />}
                  label="WhatsApp"
                  connected={!!user.whatsapp_phone_number}
                  onConnect={handleConnectWhatsApp}
                  onDisconnect={handleDisconnectWhatsApp}
                  className="whatsapp"
                />
                <IntegrationButton
                  icon={<FaRobot />}
                  label="Chatbot"
                  connected={true}
                  onConnect={() => navigate('/chatbot-settings')}
                  onDisconnect={() => navigate('/chatbot-settings')}
                  className="chatbot"
                />
                <IntegrationButton
                  icon={<FaWarehouse />}
                  label="Unanimity WMS"
                  connected={!!user.unanimity_wms_id}
                  onConnect={handleConnectWMS}
                  onDisconnect={() => {}}
                  loading={isWmsConnecting}
                  className="wms"
                />
                <IntegrationButton
                  icon={<FaEnvelope />}
                  label="Outlook"
                  connected={outlookAccounts.length > 0}
                  onConnect={handleConnectOutlook}
                  onDisconnect={handleConnectOutlook} // Allow connecting more accounts even when some exist
                  className="outlook"
                />
                <div className="shopify-webhook-section">
                  <div className="shopify-webhook-buttons">
                    <div className="channel-wrapper">
                      <button 
                        className={`channel-circle shopify register ${!!user.shop_name ? 'connected' : ''}`} 
                        onClick={handleRegisterOrderWebhook}
                        disabled={isRegisteringWebhook || isRemovingWebhook || isRegisteringSaveOnlyWebhook}
                        title="Register Shopify Order Webhook"
                      >
                        <FaPlus />
                      </button>
                      <span className="channel-label">Register Webhook</span>
                    </div>
                    <div className="channel-wrapper">
                      <button 
                        className={`channel-circle shopify proactive ${!!user.shop_name ? 'connected' : ''}`} 
                        onClick={handleRegisterSaveOnlyWebhook}
                        disabled={isRegisteringWebhook || isRemovingWebhook || isRegisteringSaveOnlyWebhook || !user.shop_name}
                        title="Register Shopify Save-Only Order Webhook"
                      >
                        <FaShoppingCart />
                      </button>
                      <span className="channel-label">Save-Only Webhook</span>
                    </div>
                    <div className="channel-wrapper">
                      <button 
                        className={`channel-circle shopify remove ${!!user.shop_name ? 'connected' : ''}`} 
                        onClick={handleRemoveOrderWebhook}
                        disabled={isRegisteringWebhook || isRemovingWebhook || isRegisteringSaveOnlyWebhook || !user.shop_name}
                        title="Remove Shopify Order Webhooks"
                      >
                        <FaTimes />
                      </button>
                      <span className="channel-label">Remove Webhooks</span>
                    </div>
                  </div>
                  <span className="integration-section-title">Shopify Webhooks</span>
                </div>
              </div>
            </div>

            {/* Connected Outlook Accounts Section */}
            {outlookAccounts.length > 0 && (
              <div className="connected-accounts-section">
                <h3 className="section-subtitle">
                  <FaEnvelope /> {t('userParameter.connectedOutlookAccounts', 'Connected Outlook Accounts')}
                </h3>
                {isLoadingOutlookAccounts ? (
                  <div className="loading-accounts">{t('userParameter.loadingAccounts', 'Loading accounts...')}</div>
                ) : (
                  <div className="accounts-list">
                    {outlookAccounts.map((account, index) => (
                      <div key={index} className="account-item">
                        <div className="account-info">
                          <div className="account-email">
                            <FaEnvelope className="account-icon" />
                            <span>{account.outlook_email}</span>
                          </div>
                          <div className="account-details">
                            <span className="account-added">
                              {t('userParameter.addedOn', 'Added on')}: {new Date(account.added_at).toLocaleDateString()}
                            </span>
                            {account.subscription_expiration && (
                              <span className="account-expires">
                                {t('userParameter.expiresOn', 'Expires')}: {new Date(account.subscription_expiration).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          className="remove-account-button"
                          onClick={() => handleDisconnectOutlook(account.outlook_email)}
                          title={t('userParameter.removeAccount', 'Remove account')}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="danger-zone">
              <h2 className="section-title">{t('userParameter.dangerZone')}</h2>
              <button className="delete-account-button" onClick={toggleDeletePopup}>
                <FaTrash /> {t('userParameter.deleteAccount')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="error">{t('userParameter.loadError')}</div>
      )}

      {/* Modals */}
      {isPasswordPopupVisible && (
        <Modal onClose={togglePasswordPopup} title={t('userParameter.changePassword')}>
          <form onSubmit={handlePasswordSubmit} className="password-form">
            <div className="form-group">
              <label htmlFor="current-password">{t('userParameter.currentPassword')}</label>
              <input
                type="password"
                id="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="new-password">{t('userParameter.newPassword')}</label>
              <input
                type="password"
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirm-new-password">{t('userParameter.confirmNewPassword')}</label>
              <input
                type="password"
                id="confirm-new-password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="primary-button">
                {t('userParameter.updatePassword')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {isDeletePopupVisible && (
        <Modal onClose={toggleDeletePopup} title={t('userParameter.deleteAccount')}>
          <form onSubmit={handleDeleteUser} className="delete-account-form">
            <p className="warning-text">{t('userParameter.deleteAccountWarning')}</p>
            <div className="form-group">
              <label htmlFor="confirm-username">{t('userParameter.confirmUsername')}</label>
              <input
                type="text"
                id="confirm-username"
                value={confirmUsername}
                onChange={(e) => setConfirmUsername(e.target.value)}
                required
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="danger-button">
                {t('userParameter.confirmDelete')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showWhatsAppIntegration && (
        <Modal onClose={handleCloseWhatsAppIntegration} title={t('userParameter.whatsAppIntegration')}>
          <WhatsAppIntegration />
        </Modal>
      )}

      {showChatbotSettings && (
        <Modal onClose={handleCloseChatbotSettings} title={t('userParameter.chatbotSettings')}>
          <ChatbotSettings />
        </Modal>
      )}
    </div>
  );
};

const IntegrationButton = ({ icon, label, connected, onConnect, onDisconnect, loading, className }) => {
  const handleClick = connected ? onDisconnect : onConnect;
  
  return (
    <div className="channel-wrapper">
      <button 
        className={`channel-circle ${className} ${connected ? 'connected' : ''}`} 
        onClick={handleClick} 
        disabled={loading}
      >
        {icon}
      </button>
      <span className="channel-label">{label}</span>
    </div>
  );
};

const Modal = ({ children, onClose, title }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-content large-modal">
        <h2>{title}</h2>
        <button className="close-button-circle" onClick={onClose}></button>
        {children}
      </div>
    </div>
  );
};

export default UserParameter;